import { useEffect, useRef, useMemo } from 'react'
import { format } from 'date-fns'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { useLogs } from '@/hooks/use-logs'
import { useSSE, SSEMessage } from '@/hooks/use-sse'
import type { RunDetail, LogEntry } from '@/api/types'

interface LogsTabProps {
  run: RunDetail
}

/**
 * Level to color class mapping.
 */
const levelColors: Record<string, string> = {
  info: 'text-blue-600',
  warn: 'text-yellow-600',
  error: 'text-red-600',
  debug: 'text-gray-500',
}

const levelBadgeVariants: Record<string, 'default' | 'secondary' | 'destructive' | 'warning'> = {
  info: 'default',
  warn: 'warning',
  error: 'destructive',
  debug: 'secondary',
}

/**
 * Convert SSE message to LogEntry format.
 */
function sseToLogEntry(msg: SSEMessage): LogEntry | null {
  if (msg.type === 'log' || msg.type === 'agent_progress') {
    return {
      timestamp: msg.timestamp || new Date().toISOString(),
      level: msg.level || 'info',
      message: msg.message,
      agent: msg.agent,
    }
  }
  return null
}

export function LogsTab({ run }: LogsTabProps) {
  const containerRef = useRef<HTMLDivElement>(null)

  // Determine if run is active (needs SSE)
  const isActive = run.status === 'processing' || run.status === 'queued'

  // Fetch historical logs for completed runs
  const { data: historicalLogs = [] } = useLogs(run.fileId, !isActive)

  // SSE for active runs
  const { messages: sseMessages, connectionState } = useSSE(run.fileId, {
    enabled: isActive,
    autoCloseOnComplete: true,
  })

  // Convert SSE messages to log entries
  const sseLogs = useMemo(
    () => sseMessages.map(sseToLogEntry).filter((log): log is LogEntry => log !== null),
    [sseMessages]
  )

  // Combine logs: use SSE logs for active runs, historical for completed
  const logs = isActive ? sseLogs : (run.logs || historicalLogs)

  // Auto-scroll to bottom on new logs
  useEffect(() => {
    if (containerRef.current && isActive) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight
    }
  }, [logs.length, isActive])

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Logs</CardTitle>
        {isActive && (
          <Badge variant={connectionState === 'connected' ? 'success' : 'secondary'}>
            {connectionState === 'connected' ? 'Live' : connectionState}
          </Badge>
        )}
      </CardHeader>
      <CardContent>
        {logs.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {isActive ? 'Waiting for logs...' : 'No logs available for this run.'}
          </p>
        ) : (
          <div
            ref={containerRef}
            className="space-y-1 font-mono text-sm max-h-[500px] overflow-y-auto"
          >
            {logs.map((log, idx) => (
              <div
                key={`${log.timestamp}-${idx}`}
                className="flex gap-3 py-1.5 border-b border-border/50 last:border-0"
              >
                <span className="text-muted-foreground whitespace-nowrap shrink-0">
                  {format(new Date(log.timestamp), 'HH:mm:ss.SSS')}
                </span>
                <Badge
                  variant={levelBadgeVariants[log.level] || 'default'}
                  className="shrink-0 w-14 justify-center text-xs"
                >
                  {log.level.toUpperCase()}
                </Badge>
                {log.agent && (
                  <span className="text-purple-600 font-semibold shrink-0">
                    [{log.agent}]
                  </span>
                )}
                <span className={`flex-1 ${levelColors[log.level] || ''}`}>
                  {log.message}
                </span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
